import UIIconButton, {
  UIIconButtonProps,
} from 'UIComponents/button/UIIconButton';
import styled, { css } from 'foundations-theming-specialty/utils';
import { ComponentType } from 'react';
import { AIBaseIconButtonProps } from '../AIBaseIconButton';

const getSizeShortHand = (
  size: AIBaseIconButtonProps['size']
): 'xs' | 'sm' | 'md' => {
  switch (size) {
    case 'extra-small':
      return 'xs';
    case 'small':
      return 'sm';
    case 'medium':
      return 'md';
    default:
      return size || 'md';
  }
};

const CssBaseIcon = css`
  position: relative;
  display: block;
  border-radius: 2rem !important;
  border: none !important;
  margin: ${({ theme }) => theme.space['0']} !important;
  padding: ${({ theme }) => theme.space['0']} !important;
  line-height: 0 !important;
  & * {
    line-height: 0 !important;
  }
`;

export const getComputedFontSize = (size: AIBaseIconButtonProps['size']) => {
  const shorthandSize = getSizeShortHand(size);
  switch (shorthandSize) {
    case 'xs':
      return 24;
    case 'sm':
      return 32;
    case 'md':
      return 40;
    default:
      return 40;
  }
};

const getIconFontSize = (size: UIIconButtonProps['size']) => {
  const sizePx = getComputedFontSize(size);
  if (size) {
    return css`
      font-size: calc(${sizePx}px - 8px) !important;
      width: ${sizePx}px !important;
      height: ${sizePx}px !important;
      & > span {
        position: absolute;
        left: ${({ theme }) => theme.space['0']};
        top: ${({ theme }) => theme.space['0']};
        width: 100%;
        height: 100%;
        & > span {
          position: absolute;
          left: ${({ theme }) => theme.space['0']};
          top: 50%;
          width: 100%;
          height: 100%;
        }
      }
      > span[data-icon-name='breezeRegenerate'] > span {
        top: calc(50% - ${({ theme }) => theme.space['25']});
        font-size: calc(${sizePx}px - 7px) !important;
      }
    `;
  }
};

export const BreezeTransparentAIBaseIconButton = styled<
  ComponentType<UIIconButtonProps>
>(UIIconButton)`
  ${CssBaseIcon}
  ${({ size }) => getIconFontSize(size)}
  &:not(.disabled) {
    color: ${({ theme }) => theme.color['icon-core-default']} !important;
  }
  &:hover {
    color: ${({ theme }) =>
      theme.color['breeze-fill-primary-default']} !important;
  }
  &:active {
    color: ${({ theme }) =>
      theme.color['fill-accent-neutral-hover']} !important;
  }

  &&.disabled,
  &&[aria-disabled='true'] {
    color: ${({ theme }) => theme.color['icon-core-disabled']} !important;
  }
`;
export const BreezeLinkOnDarkAIBaseIconButton = styled<
  ComponentType<UIIconButtonProps>
>(UIIconButton)`
  ${CssBaseIcon}
  ${({ size }) => getIconFontSize(size)}
  &:not(.disabled) {
    color: ${({ theme }) => theme.color['fill-surface-default']} !important;
  }
  &&.disabled {
    color: ${({ theme }) => theme.color['icon-core-disabled']} !important;
  }
  &:hover {
    color: ${({ theme }) =>
      theme.color['breeze-fill-loading-dot-default']} !important;
  }
  &:active {
    color: ${({ theme }) =>
      theme.color['breeze-fill-secondary-hover']} !important;
  }
`;

export const getStyledAIIconButton = (
  use: AIBaseIconButtonProps['use'] | UIIconButtonProps['use']
) => {
  switch (use) {
    case 'transparent':
      return BreezeTransparentAIBaseIconButton;
    case 'link-on-dark':
      return BreezeLinkOnDarkAIBaseIconButton;
    default:
      return BreezeTransparentAIBaseIconButton;
  }
};
